import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as HighchartsExporting from 'highcharts/modules/exporting';

// Initialize exporting module safely
if (typeof HighchartsExporting === 'function') {
    HighchartsExporting(Highcharts);
} else if (HighchartsExporting && typeof HighchartsExporting.default === 'function') {
    HighchartsExporting.default(Highcharts);
}
import api from '../utils/api';
import { FiClock, FiX, FiCalendar, FiArrowRight, FiDownload, FiEye, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/** Parse backend date strings (d-m-Y, e.g. 01-07-2026) without locale misread. */
const parseDetailDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = String(dateStr).trim().split('-');
    if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (day && month && year) {
            return new Date(year, month - 1, day);
        }
    }
    const fallback = new Date(dateStr);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const MissingStatusReport = ({ year, month, isHROrAdmin, userId }) => {
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [selectedYear, setSelectedYear] = useState(year);
    const [selectedMonth, setSelectedMonth] = useState(month);
    const [visibilityFilter, setVisibilityFilter] = useState('visible');
    const [toggling, setToggling] = useState(false);
    const [checkedDates, setCheckedDates] = useState([]);
    const [savingDates, setSavingDates] = useState(false);

    const yearOptions = [];
    const startYear = 2026;
    const currentYr = new Date().getFullYear();
    // Start at 2026 and continue up to next year automatically
    for (let i = startYear; i <= Math.max(startYear + 5, currentYr + 1); i++) yearOptions.push(i);

    const fetchReport = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const res = await api.get('/hrms/missing-status-report', {
                params: { year: selectedYear, month: selectedMonth, user_id: userId }
            });
            if (res.data.status) {
                setReportData(res.data.data);
                if (userId && res.data.data.length > 0) {
                    const emp = res.data.data.find(e => e.user_id === userId) || res.data.data[0];
                    setSelectedEmployee(emp);
                }
                return res.data.data;
            }
        } catch (error) {
            console.error('Error fetching missing status report:', error);
        } finally {
            if (!silent) setLoading(false);
        }
        return null;
    };

    const handleToggleVisibility = async (employee) => {
        try {
            setToggling(true);
            const newFlag = employee.attendance_flag === 1 ? 0 : 1;
            const res = await api.post('/hrms/toggle-attendance-flag', {
                user_id: employee.user_id,
                flag: newFlag
            });

            if (res.data.status) {
                toast.success(`Employee ${newFlag === 1 ? 'visible' : 'hidden'} now`);
                // Update local state
                setReportData(prev => prev.map(emp =>
                    emp.user_id === employee.user_id
                        ? { ...emp, attendance_flag: newFlag }
                        : emp
                ));
                setSelectedEmployee(prev => prev && prev.user_id === employee.user_id
                    ? { ...prev, attendance_flag: newFlag }
                    : prev
                );
            }
        } catch (error) {
            console.error('Error toggling visibility:', error);
            toast.error('Failed to update visibility');
        } finally {
            setToggling(false);
        }
    };

    const handleToggleDateCheck = (date) => {
        setCheckedDates(prev =>
            prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
        );
    };

    const handleSelectAllDates = () => {
        if (!selectedEmployee?.details?.length) return;
        const allDates = selectedEmployee.details.map(d => d.date);
        const allSelected = allDates.every(d => checkedDates.includes(d));
        setCheckedDates(allSelected ? [] : allDates);
    };

    const handleSaveDismissedDates = async () => {
        if (!selectedEmployee || checkedDates.length === 0) {
            toast.error('Please select at least one date');
            return;
        }

        try {
            setSavingDates(true);
            const res = await api.post('/hrms/mark-missing-status-sent', {
                user_id: selectedEmployee.user_id,
                dates: checkedDates
            });

            if (!res.data.status || !res.data.processed) {
                toast.error(res.data.message || 'No tasks found for the selected dates');
                return;
            }

            const savedCount = res.data.processed;
            const empId = selectedEmployee.user_id;
            setCheckedDates([]);

            const freshData = await fetchReport(true);
            const updated = freshData?.find(e => e.user_id === empId);

            if (!updated || updated.missed_count === 0) {
                setShowModal(false);
                setSelectedEmployee(null);
            } else {
                setSelectedEmployee(updated);
            }

            toast.success(res.data.message || `${savedCount} date(s) removed successfully`);
        } catch (error) {
            console.error('Error marking missing status as sent:', error);
            toast.error(error.response?.data?.message || 'Failed to remove selected dates');
        } finally {
            setSavingDates(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!selectedEmployee) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Missing Reports');

        // Title
        worksheet.mergeCells('A1:C1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = `Missing Status Report - ${selectedEmployee.name}`;
        titleCell.font = { name: 'Arial', size: 14, bold: true };
        titleCell.alignment = { horizontal: 'center' };

        // Employee Info
        worksheet.mergeCells('A2:C2');
        worksheet.getCell('A2').value = `Month: ${monthNames[selectedMonth - 1]} ${selectedYear}`;
        worksheet.getCell('A2').alignment = { horizontal: 'center' };

        worksheet.addRow([]);

        // Header
        worksheet.addRow(['Employee ID', 'Missing Date', 'Day']);
        const headerRow = worksheet.getRow(4);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFEEEEEE' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Data Rows
        selectedEmployee.details.forEach(detail => {
            const dateObj = parseDetailDate(detail.date);
            worksheet.addRow([
                selectedEmployee.user_id,
                detail.date,
                dateObj ? dateObj.toLocaleDateString('en-GB', { weekday: 'long' }) : detail.date
            ]);
        });

        // Summary Table
        worksheet.addRow([]);
        worksheet.addRow([]);

        const summaryHeaderRow = worksheet.addRow(['Summary', 'Count']);
        summaryHeaderRow.font = { bold: true };
        summaryHeaderRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
        summaryHeaderRow.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };

        worksheet.addRow(['Total Missing Days', selectedEmployee.missed_count]);

        // Auto-width columns
        worksheet.columns.forEach(column => {
            column.width = 20;
        });

        // Generate Buffer
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Missing_Report_${selectedEmployee.name}_${selectedMonth}_${selectedYear}.xlsx`);
    };

    const handleDownloadAllExcel = async () => {
        if (!filteredData || filteredData.length === 0) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('All Missing Reports');

        // 1. Company Name (Row 1)
        worksheet.mergeCells('A1:D1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = 'Enova Software and Hardware Solutions P Ltd.';
        companyCell.font = { name: 'Calibri', size: 18, bold: true };
        companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 35; // Increased height
        worksheet.getRow(2).height = 25;

        // 2. Report Title (Row 2) - Red Header
        worksheet.mergeCells('A2:D2');
        const subHeaderCell = worksheet.getCell('A2');
        subHeaderCell.value = `Consolidated Missing Status Report - ${monthNames[selectedMonth - 1]} ${selectedYear}`;
        subHeaderCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; // White text
        subHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC00000' } }; // Dark Red background
        subHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };

        // 3. Table Headers (Dark Blue)
        const headerRow = worksheet.addRow(['Employee ID', 'Employee Name', 'Missed Count', 'Missing Dates']);
        headerRow.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } }; // White text
        headerRow.height = 25;

        ['A', 'B', 'C', 'D'].forEach(col => {
            const cell = worksheet.getCell(`${col}3`);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF232F3E' } }; // Dark Blue/Black
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            if (col === 'B' || col === 'D') cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        });

        // 4. Data Rows with Zebra Striping
        let totalMisses = 0;
        filteredData.forEach((emp, index) => {
            const dates = emp.details.map(d => d.date).join(', ');
            const row = worksheet.addRow([emp.user_id, emp.name, emp.missed_count, dates]);

            totalMisses += emp.missed_count;

            // Alternating Row Color (Light Pink)
            if (index % 2 !== 0) {
                row.eachCell((cell) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEBEB' } }; // Light Pink
                });
            }

            // Styling
            row.height = 30; // Taller rows
            row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' }; // ID
            row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }; // Name
            row.getCell(2).font = { name: 'Calibri', size: 11 };

            // Count - Red & Bold
            const countCell = row.getCell(3);
            countCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFF0000' } }; // Red
            countCell.alignment = { horizontal: 'center', vertical: 'middle' };

            // Dates
            row.getCell(4).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true, indent: 1 };
            row.getCell(4).font = { name: 'Calibri', size: 10 };
        });

        // 5. Total Row
        const totalRow = worksheet.addRow(['', 'TOTAL', totalMisses, '']);
        totalRow.height = 25;

        // Style Total Row (Dark Blue like header)
        ['A', 'B', 'C', 'D'].forEach(col => {
            const cell = totalRow.getCell(col);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF232F3E' } };
            cell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Merge A & B for "TOTAL" text
        const trNum = totalRow.number;
        worksheet.mergeCells(`A${trNum}:B${trNum}`);
        totalRow.getCell(1).value = 'TOTAL';

        // 6. Borders for entire table
        const lastRow = totalRow.number;
        for (let r = 3; r <= lastRow; r++) {
            const row = worksheet.getRow(r);
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFDDDDDD' } }, // Light gray borders
                    bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } }
                };
            });
        }

        // 7. Column Widths
        worksheet.columns = [
            { width: 15 }, // ID
            { width: 30 }, // Name
            { width: 15 }, // Count
            { width: 80 }  // Dates
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Enova_Consolidated_Missing_Report_${selectedMonth}_${selectedYear}.xlsx`);
    };


    useEffect(() => {
        if (isHROrAdmin || userId) {
            fetchReport();
        }
    }, [selectedYear, selectedMonth, isHROrAdmin, userId]);

    // Sync with props when they change (e.g. from global filters)
    useEffect(() => { setSelectedYear(year); }, [year]);
    useEffect(() => { setSelectedMonth(month); }, [month]);

    useEffect(() => {
        if (showModal && selectedEmployee) {
            setCheckedDates([]);
        }
    }, [showModal, selectedEmployee?.user_id]);

    useEffect(() => {
        if (userId && reportData.length > 0) {
            const emp = reportData.find(e => e.user_id === userId) || reportData[0];
            setSelectedEmployee(emp);
        }
    }, [userId, reportData]);

    const filteredData = reportData.filter(d => visibilityFilter === 'visible' ? d.attendance_flag === 1 : d.attendance_flag === 0);

    if (!isHROrAdmin && !userId) return null;

    const chartOptions = {
        exporting: {
            enabled: true,
            sourceWidth: 1200,
            chartOptions: {
                chart: { backgroundColor: '#ffffff' },
                title: {
                    text: 'Missed status count (this month) until yesterday',
                    style: { fontSize: '20px', fontWeight: 'bold', color: '#1f2937', fontFamily: 'Arial' }
                }
            }
        },
        chart: {
            type: 'column',
            height: 300,
            backgroundColor: 'transparent',
            spacingTop: 50, // More space for avatars
            spacingBottom: 20,
            scrollablePlotArea: {
                minWidth: 700, // Force horizontal scroll on mobile
                scrollPositionX: 0
            }
        },
        title: { text: null },
        xAxis: {
            categories: filteredData.map(d => d.name),
            title: {
                text: 'Employee Name (Click names for details)',
                style: { fontSize: '9px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' },
                margin: 20
            },
            labels: {
                step: 1, // Show all labels regardless of space
                style: { fontSize: '11px', fontWeight: 'bold', color: '#64748b', cursor: 'pointer' },
                formatter: function () {
                    return this.value.split(' ')[0]; // Show first name only
                },
                events: {
                    click: function () {
                        const empName = this.value;
                        const emp = filteredData.find(d => d.name === empName);
                        if (emp) {
                            setSelectedEmployee(emp);
                            setShowModal(true);
                        }
                    }
                }
            },
            lineWidth: 1,
            lineColor: '#e2e8f0',
            tickWidth: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Count',
                style: { fontSize: '9px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }
            },
            gridLineWidth: 0, // No horizontal lines
            labels: { enabled: false }, // Hide Y scale labels
            tickInterval: 1
        },
        tooltip: {
            useHTML: true,
            outside: true, // Prevent overlap with avatars
            padding: 0,
            backgroundColor: 'transparent',
            borderWidth: 0,
            shadow: false,
            formatter: function () {
                const emp = filteredData[this.point.index];
                // Show only dates (backend now sends only dates in details)
                const missedDatesPreview = emp.details.slice(0, 3).map(d => `<li>${d.date}</li>`).join('');
                const moreText = emp.details.length > 3 ? `<li>...and ${emp.details.length - 3} more</li>` : '';

                return `
                <div style="padding: 12px; background: white; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); min-width: 150px;">
                    <b style="font-size: 13px; color: #1e293b; display: block; margin-bottom: 4px;">${emp.name}</b>
                    <div style="color: #ef4444; font-weight: 800; font-size: 11px; margin-bottom: 8px;">
                        ${this.y} Missed Submissions
                    </div>
                    <ul style="margin: 0; padding-left: 12px; font-size: 10px; color: #64748b; list-style-type: disc;">
                        ${missedDatesPreview}
                        ${moreText}
                    </ul>
                </div>
            `;
            }
        },
        plotOptions: {
            column: {
                borderRadius: 8, // Rounded top corners
                colorByPoint: true,
                colors: ['#7dd3fc', '#fcd34d', '#f87171', '#67e8f9', '#a78bfa', '#fb923c', '#4ade80'],
                pointPadding: 0.15,
                groupPadding: 0.1,
                borderWidth: 0,
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            const emp = filteredData[this.index];
                            setSelectedEmployee(emp);
                            setShowModal(true);
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    crop: false,
                    overflow: 'allow',
                    allowOverlap: true,
                    formatter: function () {
                        const emp = filteredData[this.point.index];
                        let avatarUrl = null;

                        if (emp?.profile_image) {
                            if (emp.profile_image.startsWith('http')) {
                                avatarUrl = emp.profile_image;
                            } else {
                                const basePath = emp.profile_image.startsWith('uploads/') ? '' : 'uploads/profiles/';
                                avatarUrl = `${process.env.REACT_APP_API_URL || ''}/${basePath}${emp.profile_image}`;
                            }
                        }

                        avatarUrl = avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random`;

                        return `
                        <div style="text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 32px; height: 32px; position: relative; top: -10px;">
                            <div style="width: 28px; height: 28px; border-radius: 50%; border: 2px solid white; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; background: #fff; display: flex; align-items: center; justify-content: center;">
                                <img src="${avatarUrl}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.src='https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name)}&background=random'" />
                            </div>
                            <div style="background: white; color: #475569; font-size: 9px; font-weight: 900; padding: 1px 4px; border-radius: 4px; position: absolute; bottom: -8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); border: 1px solid #f1f5f9;">${this.y}</div>
                        </div>
                    `;
                    },
                    verticalAlign: 'top',
                    inside: false,
                    padding: 0,
                    y: -45 // Position above the bar
                }
            }
        },
        series: [{
            name: 'Missed Days',
            data: filteredData.map(d => ({
                y: d.missed_count,
                color: d.attendance_flag === 0 ? '#cbd5e1' : undefined // Grey out if hidden
            })),
            showInLegend: false
        }],
        credits: { enabled: false }
    };

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition border border-gray-100 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-xl text-red-500 shadow-sm border border-red-100">
                        <FiClock size={20} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-black text-gray-900 leading-tight">Missed status count (this month) until yesterday</h3>
                            <button
                                onClick={handleDownloadAllExcel}
                                title="Download All Data Excel"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition shadow-sm"
                            >
                                <FiDownload size={14} />
                                <span>Export</span>
                            </button>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{monthNames[selectedMonth - 1]} {selectedYear}</p>
                        </div>
                    </div>
                </div>

                {/* Local Filters */}
                <div className="flex items-center gap-2">
                    <select
                        value={visibilityFilter}
                        onChange={(e) => setVisibilityFilter(e.target.value)}
                        className="text-[10px] font-black uppercase text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-200 transition-all cursor-pointer pr-8"
                    >
                        <option value="visible">Visible</option>
                        <option value="hidden">Hidden</option>
                    </select>
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        className="text-xs font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-200 transition-all cursor-pointer pr-8"
                    >
                        {monthNames.map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                        ))}
                    </select>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="text-xs font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-200 transition-all cursor-pointer pr-8"
                    >
                        {yearOptions.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex-1 relative min-h-[300px] mt-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    </div>
                ) : userId && selectedEmployee ? (
                    /* Single Employee View (No Chart) */
                    <div className="flex flex-col h-full animate-in fade-in duration-500">
                        <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                            <div className="w-16 h-16 rounded-full border-4 border-white shadow-sm overflow-hidden bg-white">
                                <img
                                    src={selectedEmployee.profile_image?.startsWith('http')
                                        ? selectedEmployee.profile_image
                                        : `${process.env.REACT_APP_API_URL || ''}/${selectedEmployee.profile_image?.startsWith('uploads/') ? '' : 'uploads/profiles/'}${selectedEmployee.profile_image}`}
                                    className="w-full h-full object-cover"
                                    alt=""
                                    onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedEmployee.name)}&background=random`; }}
                                />
                            </div>
                            <div>
                                <h4 className="text-xl font-black text-slate-800">{selectedEmployee.name}</h4>
                                <p className="text-xs font-black text-red-500 uppercase tracking-widest flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                                    {selectedEmployee.missed_count} Missing Reports in {monthNames[selectedMonth - 1]}
                                </p>
                            </div>
                            <button
                                onClick={handleDownloadExcel}
                                className="ml-auto flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black hover:bg-slate-800 transition-all shadow-lg active:scale-95 uppercase tracking-widest"
                            >
                                <FiDownload size={14} />
                                Export PDF
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 overflow-y-auto pr-2 max-h-[400px]">
                            {selectedEmployee.details.map((detail, idx) => {
                                const dateObj = parseDetailDate(detail.date);
                                return (
                                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-2xl border border-slate-100 shadow-sm hover:border-red-200 transition-all group">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-red-50 flex flex-col items-center justify-center text-red-500 group-hover:bg-red-500 group-hover:text-white transition-all">
                                            <span className="text-[8px] font-black leading-none uppercase">{dateObj ? dateObj.toLocaleDateString('en-GB', { month: 'short' }) : ''}</span>
                                            <span className="text-sm font-black leading-none">{dateObj ? dateObj.getDate() : ''}</span>
                                        </div>
                                        <div>
                                            <span className="text-xs font-black text-slate-700 block">{dateObj ? dateObj.toLocaleDateString('en-GB', { weekday: 'long' }) : detail.date}</span>
                                            <span className="text-[9px] font-bold text-slate-400 uppercase">Daily Status Report</span>
                                        </div>
                                    </div>
                                    <div className="w-2 h-2 rounded-full bg-red-400"></div>
                                </div>
                            );})}
                        </div>
                    </div>
                ) : filteredData.length > 0 ? (
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <div className="bg-gray-50 p-6 rounded-full mb-4 border border-gray-100">
                            <FiClock className="text-5xl text-gray-200" />
                        </div>
                        <p className="font-black text-gray-400 uppercase tracking-widest text-xs">All caught up!</p>
                        <p className="text-xs text-gray-300 mt-1">No missing status reports for this period.</p>
                    </div>
                )}
            </div>

            {/* Compact Details Modal - Only show if NO userId is provided */}
            {!userId && showModal && selectedEmployee && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[60] p-4 group">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-[320px] w-full overflow-hidden animate-in fade-in zoom-in duration-200 relative">
                        {/* Cross Icon - Absolute Top Right */}
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all z-10"
                        >
                            <FiX size={20} />
                        </button>

                        {/* Header with Profile */}
                        <div className="p-6 pb-4 flex flex-col items-center text-center mt-2">
                            <div className="relative mb-3">
                                <div className="w-20 h-20 rounded-full border-4 border-red-50 shadow-md overflow-hidden bg-gray-50">
                                    <img
                                        src={selectedEmployee.profile_image?.startsWith('http')
                                            ? selectedEmployee.profile_image
                                            : `${process.env.REACT_APP_API_URL || ''}/${selectedEmployee.profile_image?.startsWith('uploads/') ? '' : 'uploads/profiles/'}${selectedEmployee.profile_image}`}
                                        className="w-full h-full object-cover"
                                        alt=""
                                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedEmployee.name)}&background=random`; }}
                                    />
                                </div>
                                <div className="absolute -bottom-1 -right-1 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full border-2 border-white shadow-sm">
                                    {selectedEmployee.missed_count}
                                </div>
                            </div>

                            <h4 className="text-lg font-black text-gray-900 leading-tight">{selectedEmployee.name}</h4>
                            <div className="flex items-center gap-3 mt-1">
                                <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest">Missing Report Submissions</p>
                                <div className="relative group flex items-center justify-center">
                                    <button
                                        onClick={() => handleToggleVisibility(selectedEmployee)}
                                        disabled={toggling}
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-black transition-all ${selectedEmployee.attendance_flag === 1
                                            ? 'bg-green-50 border-green-200 text-green-600 shadow-sm'
                                            : 'bg-gray-50 border-gray-200 text-gray-400'}`}
                                    >
                                        {selectedEmployee.attendance_flag === 1 ? <FiEye size={10} /> : <FiEyeOff size={10} />}
                                        {selectedEmployee.attendance_flag === 1 ? 'VISIBLE' : 'HIDDEN'}
                                    </button>
                                    {/* Custom Tooltip */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 hidden group-hover:block z-50 w-max pointer-events-none animate-in fade-in zoom-in duration-200">
                                        <div className="bg-gray-900 text-white text-[10px] font-medium py-1.5 px-3 rounded-lg shadow-xl relative">
                                            {selectedEmployee.attendance_flag === 1 ? "Click to Hide" : "Click to Visible"}
                                            {/* Arrow */}
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -mb-[1px] border-4 border-transparent border-b-gray-900"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Date List */}
                        <div className="px-6 pb-6 mt-2">
                            <div className="bg-gray-50 rounded-2xl p-4 max-h-[220px] overflow-y-auto">
                                {selectedEmployee.details.length > 0 && (
                                    <label className="flex items-center gap-2 mb-3 px-1 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={selectedEmployee.details.length > 0 && selectedEmployee.details.every(d => checkedDates.includes(d.date))}
                                            onChange={handleSelectAllDates}
                                            className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-200 cursor-pointer"
                                        />
                                        <span className="text-[10px] font-black text-gray-500 uppercase tracking-wider">Select All</span>
                                    </label>
                                )}
                                <div className="space-y-2">
                                    {selectedEmployee.details.map((detail, idx) => {
                                        const dateObj = parseDetailDate(detail.date);
                                        return (
                                        <div key={idx} className="flex items-center justify-between py-2 px-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-red-50 flex flex-col items-center justify-center text-red-500">
                                                    <span className="text-[8px] font-black leading-none uppercase">{dateObj ? dateObj.toLocaleDateString('en-GB', { month: 'short' }) : ''}</span>
                                                    <span className="text-xs font-black leading-none">{dateObj ? dateObj.getDate() : ''}</span>
                                                </div>
                                                <span className="text-xs font-bold text-gray-600">{dateObj ? dateObj.toLocaleDateString('en-GB', { weekday: 'long' }) : detail.date}</span>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={checkedDates.includes(detail.date)}
                                                onChange={() => handleToggleDateCheck(detail.date)}
                                                className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-200 cursor-pointer shrink-0"
                                            />
                                        </div>
                                    );})}
                                    {selectedEmployee.details.length === 0 && (
                                        <p className="text-xs text-center text-gray-400 font-bold py-4">No missing dates to show</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-center mt-4">
                                <button
                                    onClick={handleSaveDismissedDates}
                                    disabled={checkedDates.length === 0 || savingDates}
                                    className="px-8 py-2 bg-red-500 text-white rounded-xl text-xs font-black hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md active:scale-95"
                                >
                                    {savingDates ? 'REMOVING...' : 'REMOVE'}
                                </button>
                            </div>

                            <button
                                onClick={handleDownloadExcel}
                                className="w-full mt-5 py-3 bg-gray-900 text-white rounded-2xl text-xs font-black hover:bg-black transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
                            >
                                <FiDownload size={16} />
                                DOWNLOAD REPORT
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MissingStatusReport;




/**missing status report */